/** @jsxImportSource @emotion/react */
"use client";

import { usePath, useSearch } from "@packages/framework";
import type { MediaPlayerProps, MediaSizes } from "@packages/media";
import { getContentImage, usePlayer } from "@packages/media";
import * as Dialog from "@radix-ui/react-dialog";
import * as Toolbar from "@radix-ui/react-toolbar";
import type { MouseEventHandler, RefObject } from "react";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RemoveScroll } from "react-remove-scroll";

import { shades, useTheme } from "../../../../../global/emotion";
import { useDynamicViewport } from "../../../../hooks/useDynamicViewport";
import { useDynamicViewportHeight } from "../../../../hooks/useDynamicViewportHeight";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import { emotionDynamics } from "../../../../lib/emotionDynamics";
import { useToastPositioning } from "../../../../providers/ToastProvider/ToastProvider.emotion";
import { colorMix } from "../../../../utils/colors/colorMix";
import type { CSSColorStrict } from "../../../../utils/colors/types/CSSColorStrict.types";
import { useFullscreenCatcher } from "../../../hooks/useFullscreenCatcher.emotion";
import {
  coreMediaStyles,
  infoStyles,
  innerContainerStyles,
  mediaContainerStyles,
  mediaStyles,
  outerContainerStyles,
  useMediaClass,
} from "../../../utils/media.emotion";
import { MediaInfo } from "../../blocks/data/MediaInfo/MediaInfo.emotion";
import { Subtitles } from "../../blocks/data/Subtitles/Subtitles.emotion";
import { GoFullscreen } from "../../blocks/groups/GoFullscreen/GoFullscreen.emotion";
import { MediaControls } from "../../blocks/groups/MediaControls/MediaControls.emotion";
import { MediaHeader } from "../../blocks/groups/MediaHeader/MediaHeader.emotion";
import { SettingsContainer } from "../../blocks/groups/SettingsContainer/SettingsContainer.emotion";

export type PlayerInternalContextShape = {
  collapsePlayer: MouseEventHandler<HTMLButtonElement>;
  expandPlayer: MouseEventHandler;
  goFullscreen: MouseEventHandler;
  exitFullscreen: MouseEventHandler;
  popupRef: RefObject<HTMLDivElement | null>;
  transcriptOpen: boolean;
  transcriptOpenChangeHandler: (open: boolean) => void;
};

export const PlayerInternalContext = createContext<PlayerInternalContextShape>({
  collapsePlayer: () => {},
  expandPlayer: () => {},
  goFullscreen: () => {},
  exitFullscreen: () => {},
  popupRef: { current: null },
  transcriptOpen: false,
  transcriptOpenChangeHandler: () => {},
});

export const MediaPlayer = ({
  children,
  mediaDetails,
  onClose,
  onCollapse,
  onExpand,
  onFullscreen,
  fullscreenContainer,
  videoWidth,
}: MediaPlayerProps) => {
  const player = usePlayer();
  const isDesktop = useDynamicViewport({ minimumWidth: 1024 });
  const isTallEnough = useDynamicViewportHeight({
    minimumHeight: 325,
    defaultState: true,
  });
  const classicIsDesktop = useIsDesktopViewport();
  const size: MediaSizes = `${player?.display ?? "hidden"}-${isDesktop ? "desktop" : "mobile"}`;
  const colorHex = getContentImage(mediaDetails)?.color_hex ?? "#000000";
  const [transcriptOpen, setTranscriptOpen] = useState<boolean>(false);
  const { setHasPlayer } = useToastPositioning();
  const path = usePath();
  const search = useSearch();

  useEffect(() => {
    if (player?.display === "expanded") {
      window?.history.pushState(null, "", `${path}?player`);
    }
  }, [player?.display]);

  useEffect(() => {
    if (player?.display === "expanded") {
      if (!search.has("player")) {
        onCollapse();
      }
    }
  }, [path, search]);

  const mediaClass = useMediaClass();
  const { themeContainerRef } = useTheme();
  const [subtitle, setSubtitle] = useState<string | null>(null);

  const popupRef = useMemo(
    () =>
      player?.display === "fullscreen"
        ? fullscreenContainer
        : themeContainerRef,
    [player?.display],
  );

  useEffect(() => {
    setHasPlayer(player?.display === "collapsed");
  }, [player?.display]);

  useEffect(() => {
    // collapse on desktop devices below a certain height
    if (!isTallEnough && classicIsDesktop) onCollapse();
  }, [isTallEnough, classicIsDesktop]);

  const colors = colorMix<"array">({
    source: colorHex,
    adjustment: player?.mediaType === "video" ? shades.shade50 : shades.shade30,
    format: "array",
  }) as number[];
  let red = 0,
    green = 0,
    blue = 0;
  if (Array.isArray(colors)) [red, green, blue] = colors;

  const handleClose: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onClose();
  };
  const handleCollapse: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onCollapse();
  };

  const handleFullscreen: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onFullscreen();
  };

  const handleExitFullscreen: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onExpand(e);
  };

  const infoStylesComputed = useMemo(
    () => infoStyles[mediaClass]({ videoWidth }),
    [mediaClass, videoWidth],
  );
  const mediaContainerStylesComputed = useMemo(
    () => mediaContainerStyles[mediaClass],
    [mediaClass],
  );
  const mediaStylesComputed = useMemo(
    () => mediaStyles[mediaClass],
    [mediaClass],
  );

  const ControlsContainer = useCallback(
    ({ children, showControls = true }) => {
      if (
        mediaClass.includes("mobile") &&
        (mediaClass.includes("expanded") ||
          mediaClass.includes("fullscreen")) &&
        (mediaClass.includes("portrait") || mediaClass.includes("audio"))
      ) {
        return (
          <div
            key={"mediaLowerSection"}
            css={[
              mediaClass.includes("landscape")
                ? coreMediaStyles.lowerSectionLandscape
                : coreMediaStyles.lowerSection,
              player?.mediaType === "video"
                ? [
                    coreMediaStyles.edgeBase,
                    coreMediaStyles.bottomEdge,
                    // background gradient here is handled by useFullscreenCatcher
                  ]
                : emotionDynamics.backgroundGradientSpread({
                    direction: "to bottom",
                    red,
                    green,
                    blue,
                    spread: 32,
                  }),
              showControls ? coreMediaStyles.edgeVisible : null,
            ]}
          >
            {children}
          </div>
        );
      }
      return <>{children}</>;
    },
    [mediaClass, player?.mediaType, red, green, blue],
  );
  const [
    catcher,
    showControls,
    handleMouseMove,
    stopHidingControls,
    resumeHidingControls,
  ] = useFullscreenCatcher();

  useEffect(() => {
    function updateSubtitleOnChange() {
      // if we're in native iOS Safari fullscreen, don't change the subtitle visibility.
      if (
        this.mode !== "hidden" &&
        // @ts-ignore
        !player.mediaRef.current.isNativeFullscreen
      ) {
        this.mode = "hidden";
      }
      if (this.activeCues?.[0])
        setSubtitle((this.activeCues[0] as VTTCue).text);
    }

    if (player.mediaRef.current && player.subtitleIdx !== null) {
      const track = player.mediaRef.current.textTracks[player.subtitleIdx];
      if (track) {
        track.mode = "hidden";
        if (track.activeCues?.[0]) {
          setSubtitle((track.activeCues[0] as VTTCue).text);
        }
        track.addEventListener("cuechange", updateSubtitleOnChange);

        return () => {
          track.removeEventListener("cuechange", updateSubtitleOnChange);
          setSubtitle(null);
        };
      }
    } else {
      setSubtitle(null);
    }
  }, [player.subtitleIdx]);

  return (
    <Dialog.Root>
      <RemoveScroll
        enabled={
          player?.display === "expanded" || player?.display === "fullscreen"
        }
      >
        <Toolbar.Root asChild>
          <Dialog.Trigger asChild>
            <Toolbar.Button asChild>
              <a
                key={"mediaOuterContainer"}
                tabIndex={player?.display !== "collapsed" ? -1 : 0}
                onClick={(e) => {
                  if (player?.display === "collapsed") onExpand(e);
                }}
                css={[
                  coreMediaStyles.outerContainer,
                  outerContainerStyles[mediaClass],
                  size === "expanded-desktop" ||
                  mediaClass === "audio-expanded-mobile-landscape"
                    ? player?.mediaType === "video"
                      ? coreMediaStyles.videoBackground
                      : emotionDynamics.colorMixBackgroundColor({
                          source: colorHex as CSSColorStrict,
                          adjustment: shades.shade30,
                        })
                    : null,
                  player?.inTransition
                    ? coreMediaStyles.containerTransition
                    : null,
                ]}
              >
                <PlayerInternalContext.Provider
                  value={{
                    collapsePlayer: onCollapse,
                    expandPlayer: onExpand,
                    goFullscreen: handleFullscreen,
                    exitFullscreen: handleExitFullscreen,
                    popupRef,
                    transcriptOpenChangeHandler: setTranscriptOpen,
                    transcriptOpen,
                  }}
                >
                  <div
                    key={"mediaClickCatcher"}
                    css={[
                      coreMediaStyles.clickCatcher,
                      player?.display === "collapsed"
                        ? coreMediaStyles.clickCatcherCollapsed
                        : null,
                    ]}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div
                    ref={fullscreenContainer}
                    key={"mediaInnerContainer"}
                    css={[
                      coreMediaStyles.innerContainer,
                      innerContainerStyles[mediaClass],
                      [
                        "expanded-desktop",
                        "expanded-mobile",
                        "fullscreen-desktop",
                        "fullscreen-mobile",
                      ].includes(size)
                        ? player?.mediaType === "video"
                          ? coreMediaStyles.videoBackground
                          : null
                        : emotionDynamics.colorMixBackgroundColor({
                            source: colorHex,
                            adjustment:
                              player?.mediaType === "video"
                                ? shades.shade50
                                : shades.shade30,
                          }),
                      size === "collapsed-mobile" && !classicIsDesktop
                        ? coreMediaStyles.innerContainerMobile
                        : null,
                      !showControls ? coreMediaStyles.hideMouse : null,
                      player?.inTransition
                        ? coreMediaStyles.containerTransition
                        : null,
                    ]}
                  >
                    <MediaHeader
                      onCollapse={handleCollapse}
                      onMouseMove={handleMouseMove}
                      showControls={showControls}
                    />
                    <div
                      key={"mediaAllDisplay"}
                      css={mediaContainerStylesComputed}
                    >
                      <div
                        key={"mediaItself"}
                        css={[
                          player?.mediaType === "video"
                            ? coreMediaStyles.video
                            : null,
                          mediaStylesComputed,
                        ]}
                      >
                        {children}
                        <Subtitles showControls={showControls}>
                          {subtitle}
                        </Subtitles>
                      </div>
                      {player?.display === "fullscreen" ||
                      (player?.display === "expanded" &&
                        player?.mediaType === "video")
                        ? catcher
                        : null}
                      {(mediaClass.includes("expanded") ||
                        mediaClass.includes("fullscreen")) &&
                      mediaClass.includes("mobile") &&
                      mediaClass.includes("portrait") &&
                      mediaClass.includes("video") ? (
                        <div
                          css={[
                            coreMediaStyles.standaloneFullscreenButtonContainer,
                            coreMediaStyles.edgeBase,
                            coreMediaStyles.topEdge,
                            showControls ? coreMediaStyles.edgeVisible : null,
                          ]}
                        >
                          <GoFullscreen
                            onFullscreen={handleFullscreen}
                            onExitFullscreen={handleExitFullscreen}
                            variant={"lighten"}
                          />
                        </div>
                      ) : null}
                      <ControlsContainer showControls={showControls}>
                        <MediaInfo
                          css={[
                            infoStylesComputed,
                            (mediaClass.includes("expanded") ||
                              mediaClass.includes("fullscreen")) &&
                            (mediaClass.includes("desktop") ||
                              mediaClass.includes("landscape"))
                              ? [
                                  coreMediaStyles.edgeBase,
                                  coreMediaStyles.topEdge,
                                ]
                              : null,
                            showControls ? coreMediaStyles.edgeVisible : null,
                          ]}
                          size={size}
                          showMenu={isDesktop || player?.display === "expanded"}
                          onFullscreen={handleFullscreen}
                          onExitFullscreen={handleExitFullscreen}
                          onMouseMove={handleMouseMove}
                          onMenuOpen={stopHidingControls}
                          onMenuClose={resumeHidingControls}
                        />
                        <MediaControls
                          size={size}
                          onClose={handleClose}
                          onMouseMove={handleMouseMove}
                          showControls={showControls}
                        />
                        {mediaClass.includes("expanded") &&
                        (mediaClass.includes("desktop") ||
                          mediaClass.includes("landscape")) &&
                        mediaClass.includes("audio") ? null : (
                          <SettingsContainer
                            size={size}
                            onMouseMove={handleMouseMove}
                            showControls={showControls}
                          />
                        )}
                      </ControlsContainer>
                    </div>
                    {mediaClass.includes("expanded") &&
                    (mediaClass.includes("desktop") ||
                      mediaClass.includes("landscape")) &&
                    mediaClass.includes("audio") ? (
                      <SettingsContainer
                        size={size}
                        onMouseMove={handleMouseMove}
                        showControls={showControls}
                      />
                    ) : null}
                  </div>
                </PlayerInternalContext.Provider>
              </a>
            </Toolbar.Button>
          </Dialog.Trigger>
        </Toolbar.Root>
      </RemoveScroll>
    </Dialog.Root>
  );
};
